import React, { Component } from "react";
import type { BaseStormcloudPlayerProps, OnProgressProps } from "./props";
import { defaultProps } from "./props";
import { isMediaStream } from "./utils";

const SEEK_ON_PLAY_EXPIRY = 5000;

export interface PlayerProps extends BaseStormcloudPlayerProps {
  activePlayer: any;
  onReady: () => void;
}

export default class Player extends Component<PlayerProps> {
  static displayName = "Player";
  static defaultProps = defaultProps;

  private mounted = false;
  private isReady = false;
  private isPlaying = false;
  private isLoading = true;
  private loadOnReady: string | null = null;
  private startOnPlay = true;
  private seekOnPlay: number | null = null;
  private onDurationCalled = false;
  private progressTimeout?: number;
  private durationCheckTimeout?: number;
  private prevPlayed?: number;
  private prevLoaded?: number;
  private player?: any;

  componentDidMount() {
    this.mounted = true;
  }

  componentWillUnmount() {
    clearTimeout(this.progressTimeout);
    clearTimeout(this.durationCheckTimeout);
    this.mounted = false;
  }

  componentDidUpdate(prevProps: PlayerProps) {
    if (!this.player) return;

    const { src, playing, volume, muted, playbackRate, loop, activePlayer } =
      this.props;

    if (prevProps.src !== src) {
      if (this.isLoading && !activePlayer.forceLoad && !isMediaStream(src)) {
        console.warn(
          `StormcloudPlayer: the attempt to load ${src} is being deferred until the player has loaded`
        );
        this.loadOnReady = src || null;
        return;
      }
      this.isLoading = true;
      this.startOnPlay = true;
      this.onDurationCalled = false;
      this.player.load(src, this.isReady);
    }

    if (!prevProps.playing && playing && !this.isPlaying) {
      this.player.play();
    }

    if (prevProps.playing && !playing && this.isPlaying) {
      this.player.pause();
    }

    if (prevProps.volume !== volume && volume !== null) {
      this.player.setVolume(volume);
    }

    if (prevProps.muted !== muted) {
      if (muted) {
        this.player.mute();
      } else {
        this.player.unmute();
        if (volume !== null) {
          setTimeout(() => this.player.setVolume(volume));
        }
      }
    }

    if (
      prevProps.playbackRate !== playbackRate &&
      this.player.setPlaybackRate
    ) {
      this.player.setPlaybackRate(playbackRate);
    }

    if (prevProps.loop !== loop && this.player.setLoop) {
      this.player.setLoop(loop);
    }
  }

  handlePlayerMount = (player: any) => {
    if (this.player) {
      this.progress();
      return;
    }

    this.player = player;
    this.player.load(this.props.src);
    this.progress();
  };

  getInternalPlayer = (key?: string) => {
    if (!this.player) return null;
    return this.player.getInternalPlayer(key);
  };

  progress = () => {
    if (this.props.src && this.player && this.isReady) {
      const playedSeconds = this.getCurrentTime() || 0;
      const loadedSeconds = this.getSecondsLoaded();
      const duration = this.getDuration();

      if (duration) {
        const progress: OnProgressProps = {
          playedSeconds,
          played: playedSeconds / duration,
          loaded: 0,
          loadedSeconds: 0,
        };

        if (loadedSeconds !== null) {
          progress.loadedSeconds = loadedSeconds;
          progress.loaded = loadedSeconds / duration;
        }

        if (
          progress.playedSeconds !== this.prevPlayed ||
          progress.loadedSeconds !== this.prevLoaded
        ) {
          this.props.onProgress?.(progress);
        }

        this.prevPlayed = progress.playedSeconds;
        this.prevLoaded = progress.loadedSeconds;
      }
    }

    this.progressTimeout = window.setTimeout(
      this.progress,
      this.props.progressInterval || 1000
    );
  };

  handleReady = () => {
    if (!this.mounted) return;

    this.isReady = true;
    this.isLoading = false;

    const { onReady, playing, volume, muted } = this.props;
    onReady();

    if (!muted && volume !== null) {
      this.player.setVolume(volume);
    }

    if (this.loadOnReady) {
      this.player.load(this.loadOnReady, true);
      this.loadOnReady = null;
    } else if (playing) {
      this.player.play();
    }

    this.handleDurationCheck();
  };

  handlePlay = () => {
    this.isPlaying = true;
    this.isLoading = false;

    const { onStart, onPlay, playbackRate } = this.props;

    if (this.startOnPlay) {
      if (this.player.setPlaybackRate && playbackRate !== 1) {
        this.player.setPlaybackRate(playbackRate);
      }
      onStart?.();
      this.startOnPlay = false;
    }

    onPlay?.();

    if (this.seekOnPlay) {
      this.seekTo(this.seekOnPlay);
      this.seekOnPlay = null;
    }

    this.handleDurationCheck();
  };

  handlePause = (e?: any) => {
    this.isPlaying = false;
    if (!this.isLoading) {
      this.props.onPause?.(e);
    }
  };

  handleEnded = () => {
    const { activePlayer, loop, onEnded } = this.props;
    if (activePlayer.loopOnEnded && loop) {
      this.seekTo(0);
    }
    if (!loop) {
      this.isPlaying = false;
      onEnded?.();
    }
  };

  handleError = (...args: any[]) => {
    this.isLoading = false;
    this.props.onError?.(args[0], args[1], args[2], args[3]);
  };

  handleDurationCheck = () => {
    clearTimeout(this.durationCheckTimeout);
    const duration = this.getDuration();
    if (duration) {
      if (!this.onDurationCalled) {
        this.props.onDuration?.(duration);
        this.onDurationCalled = true;
      }
    } else {
      this.durationCheckTimeout = window.setTimeout(
        this.handleDurationCheck,
        100
      );
    }
  };

  handleLoaded = () => {
    this.isLoading = false;
  };

  getDuration(): number | null {
    if (!this.isReady) return null;
    return this.player.getDuration();
  }

  getCurrentTime(): number | null {
    if (!this.isReady) return null;
    return this.player.getCurrentTime();
  }

  getSecondsLoaded(): number | null {
    if (!this.isReady) return null;
    return this.player.getSecondsLoaded();
  }

  seekTo(amount: number, type?: "seconds" | "fraction", keepPlaying?: boolean) {
    if (!this.isReady) {
      if (amount !== 0) {
        this.seekOnPlay = amount;
        setTimeout(() => {
          this.seekOnPlay = null;
        }, SEEK_ON_PLAY_EXPIRY);
      }
      return;
    }

    const isFraction = !type ? amount > 0 && amount < 1 : type === "fraction";
    if (isFraction) {
      const duration = this.player.getDuration();
      if (!duration) {
        console.warn(
          "StormcloudPlayer: could not seek using fraction – duration not yet available"
        );
        return;
      }
      this.player.seekTo(duration * amount, keepPlaying);
      return;
    }
    this.player.seekTo(amount, keepPlaying);
  }

  render() {
    const Player = this.props.activePlayer;
    if (!Player) {
      return null;
    }

    return React.createElement(Player, {
      ...this.props,
      onMount: this.handlePlayerMount,
      onReady: this.handleReady,
      onPlay: this.handlePlay,
      onPause: this.handlePause,
      onEnded: this.handleEnded,
      onLoaded: this.handleLoaded,
      onError: this.handleError,
    });
  }
}
